import React, { useMemo, useState } from "react";
import { ChartArea, ChartBar, ChartColumn, ChartColumnStacked, ChartLine, ChartPie, Donut } from "lucide-react";
import { Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, Legend, Line, LineChart, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { LiveBadge, formatCell } from "./ArtifactTable";

/**
 * Phase 172 — multi-style report chart for assistant artifacts.
 * Artifact: { title, type, hint, x_key, series:[{key,name}], data:[...], full_data?, y_format: inr|int|pct, alternatives:[...] }
 * Styles: bar | horizontal_bar | stacked_bar | line | area | pie | donut — the backend auto-selects; the user can switch here.
 */

// Civic token palette only — primary, success, info, warning, destructive + muted tints for extra slices.
const PALETTE = [
  "hsl(var(--primary))", "hsl(var(--success))", "hsl(var(--info))", "hsl(var(--warning))", "hsl(var(--destructive))",
  "hsl(206 60% 55%)", "hsl(150 40% 55%)", "hsl(199 60% 65%)",
];
const STATUS_COLORS = { present: "hsl(var(--success))", half_day: "hsl(var(--warning))", absent: "hsl(var(--destructive))",
  fully_paid: "hsl(var(--success))", partly_paid: "hsl(var(--warning))", unpaid: "hsl(var(--destructive))",
  assigned: "hsl(var(--primary))", unassigned: "hsl(var(--warning))", target: "hsl(206 50% 75%)", collected: "hsl(var(--primary))",
  team_goal: "hsl(206 50% 75%)", team_collected: "hsl(var(--primary))", balance: "hsl(var(--destructive))" };
const seriesColor = (key, i) => STATUS_COLORS[key] || PALETTE[i % PALETTE.length];

const STYLES = [
  { id: "bar", label: "Bar", Icon: ChartColumn },
  { id: "horizontal_bar", label: "Horizontal bar", Icon: ChartBar },
  { id: "stacked_bar", label: "Stacked bar", Icon: ChartColumnStacked },
  { id: "line", label: "Line", Icon: ChartLine },
  { id: "area", label: "Area", Icon: ChartArea },
  { id: "pie", label: "Pie", Icon: ChartPie },
  { id: "donut", label: "Donut", Icon: Donut },
];
const STYLE_LABEL = Object.fromEntries(STYLES.map((s) => [s.id, s.label]));
const PIE_MAX = 8;

const compactINR = (v) => {
  const n = Number(v || 0);
  if (Math.abs(n) >= 1e7) return `₹${(n / 1e7).toFixed(1)}Cr`;
  if (Math.abs(n) >= 1e5) return `₹${(n / 1e5).toFixed(1)}L`;
  if (Math.abs(n) >= 1e3) return `₹${(n / 1e3).toFixed(0)}k`;
  return `₹${n}`;
};
const shortLabel = (s, max = 14) => {
  const str = String(s ?? "");
  if (/^\d{4}-\d{2}$/.test(str)) return formatCell(str, "month");
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str.slice(8, 10) + "/" + str.slice(5, 7);
  return str.length > max ? str.slice(0, max - 1) + "…" : str;
};

/** Collapse to top-N slices + "Others" for pies (mirrors the backend rule). */
const toPieData = (rows, key, xKey) => {
  const sorted = rows.filter((d) => Number(d[key]) > 0).sort((a, b) => Number(b[key]) - Number(a[key]));
  if (sorted.length <= PIE_MAX) return sorted;
  const head = sorted.slice(0, PIE_MAX - 1);
  const tail = sorted.slice(PIE_MAX - 1);
  return [...head, { [xKey]: `Others (${tail.length})`, [key]: tail.reduce((s, d) => s + Number(d[key] || 0), 0) }];
};

export const ArtifactChart = ({ artifact, testId = "artifact-chart" }) => {
  const { title, type: initial = "bar", x_key: xKey = "label", series = [], data = [], full_data: fullData, y_format: yFormat = "inr", alternatives, as_of: asOf } = artifact || {};
  const [style, setStyle] = useState(initial);
  const base = useMemo(() => (fullData && fullData.length ? fullData : data) || [], [data, fullData]);
  const isPie = style === "pie" || style === "donut";
  const pieKey = series[0]?.key;
  const pieData = useMemo(() => (isPie && pieKey ? toPieData(base, pieKey, xKey) : []), [isPie, pieKey, base, xKey]);
  const pieTotal = useMemo(() => pieData.reduce((s, d) => s + Number(d[pieKey] || 0), 0), [pieData, pieKey]);

  if (!base.length || !series.length) return null;

  const fmtY = yFormat === "inr" ? compactINR : (v) => Number(v).toLocaleString("en-IN");
  const fmtTip = (v) => (yFormat === "inr" ? formatCell(Number(v), "inr") : yFormat === "pct" ? `${Number(v).toFixed(1)}%` : Number(v).toLocaleString("en-IN"));
  const allowed = new Set([...(alternatives || STYLES.map((s) => s.id)), initial]);
  if (series.length < 2) allowed.delete("stacked_bar");
  const options = STYLES.filter((s) => allowed.has(s.id));
  const many = base.length > 6;
  // horizontal bars need room per category (and per series when grouped); other styles use a fixed height
  const perRow = style === "horizontal_bar" ? (series.length > 1 ? 14 * series.length + 10 : 26) : 0;
  const chartHeight = isPie ? 240 : style === "horizontal_bar" ? Math.min(600, Math.max(220, base.length * perRow + 48)) : 224;
  const tooltipStyle = { fontSize: 12, borderRadius: 8, border: "1px solid hsl(var(--border))", background: "hsl(var(--card))" };
  const tick = { fontSize: 10, fill: "hsl(var(--muted-foreground))" };

  const renderChart = () => {
    if (isPie) {
      const RAD = Math.PI / 180;
      const inside = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
        if (percent < 0.05) return null;
        const r = innerRadius + (outerRadius - innerRadius) * (style === "donut" ? 0.5 : 0.62);
        const x = cx + r * Math.cos(-midAngle * RAD);
        const y = cy + r * Math.sin(-midAngle * RAD);
        return <text x={x} y={y} fill="#fff" textAnchor="middle" dominantBaseline="central" style={{ fontSize: 10, fontWeight: 600 }}>{`${(percent * 100).toFixed(0)}%`}</text>;
      };
      return (
        <PieChart margin={{ top: 4, right: 4, left: 4, bottom: 4 }}>
          <Pie data={pieData} dataKey={pieKey} nameKey={xKey} cx="50%" cy="50%" outerRadius="88%" innerRadius={style === "donut" ? "55%" : 0}
            paddingAngle={style === "donut" ? 2 : 1} isAnimationActive={false} stroke="hsl(var(--card))" label={inside} labelLine={false}>
            {pieData.map((d, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
          </Pie>
          <Tooltip formatter={(v, name) => [`${fmtTip(v)} (${pieTotal ? ((Number(v) * 100) / pieTotal).toFixed(1) : 0}%)`, name]} contentStyle={tooltipStyle} />
          <Legend wrapperStyle={{ fontSize: 11, maxWidth: 150 }} layout="vertical" verticalAlign="middle" align="right" formatter={(v) => shortLabel(v, 20)} />
        </PieChart>
      );
    }
    if (style === "horizontal_bar") {
      return (
        <BarChart data={base} layout="vertical" margin={{ top: 4, right: 16, left: 4, bottom: 4 }} barCategoryGap="22%">
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
          <XAxis type="number" tickFormatter={fmtY} tick={tick} />
          <YAxis type="category" dataKey={xKey} tick={tick} width={118} tickFormatter={(v) => shortLabel(v, 18)} interval={0} />
          <Tooltip formatter={(v, name) => [fmtTip(v), name]} contentStyle={tooltipStyle} cursor={{ fill: "hsl(var(--muted))", opacity: 0.4 }} />
          {series.length > 1 && <Legend wrapperStyle={{ fontSize: 11 }} />}
          {series.map((s, i) => <Bar key={s.key} dataKey={s.key} name={s.name || s.key} fill={seriesColor(s.key, i)} radius={[0, 4, 4, 0]} maxBarSize={series.length > 1 ? 12 : 22} isAnimationActive={false} />)}
        </BarChart>
      );
    }
    if (style === "line" || style === "area") {
      const Chart = style === "area" ? AreaChart : LineChart;
      return (
        <Chart data={base} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
          <XAxis dataKey={xKey} tickFormatter={shortLabel} tick={tick} interval={base.length > 12 ? "preserveStartEnd" : 0} angle={many ? -25 : 0} height={many ? 44 : 24} textAnchor={many ? "end" : "middle"} />
          <YAxis tickFormatter={fmtY} tick={tick} width={52} />
          <Tooltip formatter={(v, name) => [fmtTip(v), name]} contentStyle={tooltipStyle} />
          {series.length > 1 && <Legend wrapperStyle={{ fontSize: 11 }} />}
          {series.map((s, i) => style === "area"
            ? <Area key={s.key} type="monotone" dataKey={s.key} name={s.name || s.key} stroke={seriesColor(s.key, i)} fill={seriesColor(s.key, i)} fillOpacity={0.18} strokeWidth={2} dot={base.length <= 31} isAnimationActive={false} />
            : <Line key={s.key} type="monotone" dataKey={s.key} name={s.name || s.key} stroke={seriesColor(s.key, i)} strokeWidth={2} dot={base.length <= 31} activeDot={{ r: 4 }} isAnimationActive={false} />)}
        </Chart>
      );
    }
    const stacked = style === "stacked_bar";
    return (
      <BarChart data={base} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
        <XAxis dataKey={xKey} tickFormatter={shortLabel} tick={tick} interval={base.length > 12 ? "preserveStartEnd" : 0} angle={many ? -25 : 0} height={many ? 44 : 24} textAnchor={many ? "end" : "middle"} />
        <YAxis tickFormatter={fmtY} tick={tick} width={52} />
        <Tooltip formatter={(v, name) => [fmtTip(v), name]} contentStyle={tooltipStyle} cursor={{ fill: "hsl(var(--muted))", opacity: 0.4 }} />
        {series.length > 1 && <Legend wrapperStyle={{ fontSize: 11 }} />}
        {series.map((s, i) => <Bar key={s.key} dataKey={s.key} name={s.name || s.key} fill={seriesColor(s.key, i)} stackId={stacked ? "stack" : undefined}
          radius={stacked ? (i === series.length - 1 ? [4, 4, 0, 0] : 0) : [4, 4, 0, 0]} maxBarSize={36} isAnimationActive={false} />)}
      </BarChart>
    );
  };

  return (
    <div className="rounded-xl border bg-card shadow-sm p-3" data-testid={testId} data-chart-type={style}>
      <div className="flex items-center gap-2 mb-2">
        <ChartColumn className="h-4 w-4 text-primary shrink-0" aria-hidden="true" />
        <p className="text-xs font-semibold font-heading truncate flex-1" title={title}>{title || "Chart"}</p>
        {options.length > 1 && (
          <div className="flex items-center gap-0.5 rounded-lg border bg-muted/40 p-0.5" role="group" aria-label="Chart style" data-testid={`${testId}-styles`}>
            {options.map(({ id, label, Icon }) => (
              <button key={id} type="button" onClick={() => setStyle(id)} title={label} aria-label={label} aria-pressed={style === id} data-testid={`${testId}-switch-${id}`}
                className={`h-6 w-6 rounded-md flex items-center justify-center transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${style === id ? "bg-primary text-primary-foreground shadow-sm" : "text-muted-foreground hover:text-foreground hover:bg-card"}`}>
                <Icon className="h-3.5 w-3.5" />
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="w-full" style={{ height: chartHeight }}>
        <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 360, height: chartHeight }} debounce={50}>{renderChart()}</ResponsiveContainer>
      </div>
      <div className="flex items-center justify-between gap-2 mt-1.5">
        <p className="text-[10px] text-muted-foreground" data-testid={`${testId}-caption`}>
          {STYLE_LABEL[style] || style} chart{isPie && series.length > 1 ? ` · showing ${series[0].name || series[0].key}` : ""}{isPie && pieData.length < base.length ? ` · top ${pieData.length - 1} + Others` : ""}
        </p>
        <LiveBadge asOf={asOf} testId={`${testId}-live`} />
      </div>
    </div>
  );
};
